import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BRANDS } from '../config/brands';
import { removeLanguageFromPath } from '../utils/languageUtils';
import { isHeaderHeld } from '../utils/stickyHeader';

// On a menu page the category bar takes over the header as you scroll: the
// header slides away on the way down and returns on the way up, and the bar
// pins in its place. This hook decides when the header is hidden and publishes
// where the pinned bar belongs, as CSS variables on the root:
//   --site-header-height  the header's own height
//   --site-header-offset  that height while the header shows, 0 while it is gone
// The header stays in flow (the class only moves it with a transform), so the
// page never shifts when it comes and goes.

// The menu pages: Pizza Inn's own /menu and every brand page in the registry
// (Garden, ALL, Sədəf, Moka Land), so a new restaurant is covered the day it is
// added. Moka Land is also served at /mokaland/menu (App.js). Item pages
// (/menu/:id), the birthday and new-year menus and every other page keep the
// header they have.
const MENU_PATHS = new Set(['/menu', ...BRANDS.map((b) => b.route), '/mokaland/menu']);

export const isMenuPath = (pathname = '') => {
  const path = removeLanguageFromPath(pathname).replace(/\/+$/, '') || '/';
  return MENU_PATHS.has(path);
};

// From this width up the header is one slim row and stays where it is. Same
// breakpoint as App.css.
const DESKTOP_QUERY = '(min-width: 1100px)';
// The header leaves once the page is scrolled this far past its own height...
const NEAR_TOP_MARGIN = 40;
// ...and returns after this much scrolling back up.
const SHOW_AFTER_UP = 8;

// Overscroll on iOS bounces past the end of the page and back, which reads as
// scrolling up. Not a reason to bring the header back.
const atPageEnd = (y) => {
  const doc = document.documentElement;
  return doc.scrollHeight > window.innerHeight && window.innerHeight + y >= doc.scrollHeight - 2;
};

const listen = (mq, fn) => {
  if (mq.addEventListener) mq.addEventListener('change', fn);
  else if (mq.addListener) mq.addListener(fn);
  return () => {
    if (mq.removeEventListener) mq.removeEventListener('change', fn);
    else if (mq.removeListener) mq.removeListener(fn);
  };
};

/**
 * @param {{ current: HTMLElement | null }} headerRef the header's element
 * @param {{ locked?: boolean }} [options] locked: keep the header shown (the
 *   drawer or the mini cart, which live inside it, is open)
 * @returns {boolean} whether the header should be hidden right now
 */
export default function useHideHeaderOnScroll(headerRef, { locked = false } = {}) {
  const { pathname } = useLocation();
  const enabled = !locked && isMenuPath(pathname);
  const [hidden, setHidden] = useState(false);
  const hiddenRef = useRef(false);
  const heightRef = useRef(0);

  const publish = useCallback(() => {
    const style = document.documentElement.style;
    style.setProperty('--site-header-height', `${heightRef.current}px`);
    style.setProperty('--site-header-offset', `${hiddenRef.current ? 0 : heightRef.current}px`);
  }, []);

  const apply = useCallback(
    (next) => {
      if (hiddenRef.current === next) return;
      hiddenRef.current = next;
      setHidden(next);
      publish();
    },
    [publish]
  );

  // The header's real height, kept current: it differs by breakpoint and
  // language, so nothing here hardcodes a number.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const height = el.getBoundingClientRect().height;
      if (height === heightRef.current) return;
      heightRef.current = height;
      publish();
    };
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    window.addEventListener('resize', measure);
    measure();
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
      document.documentElement.style.removeProperty('--site-header-height');
      document.documentElement.style.removeProperty('--site-header-offset');
    };
  }, [headerRef, publish]);

  useEffect(() => {
    if (!enabled) {
      apply(false);
      return undefined;
    }
    const desktop = typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY) : null;
    let lastY = Math.max(window.scrollY, 0);
    let up = 0; // px scrolled up since the page last moved down

    const onScroll = () => {
      const y = Math.max(window.scrollY, 0);
      const dy = y - lastY;
      lastY = y;
      // Near the top the header always shows, even while the state is held: a
      // tap can jump the page back up here inside the hold's window, and when
      // the hold later expires nothing else re-evaluates. That used to leave
      // the header hidden past the top until the visitor scrolled again.
      if (y < heightRef.current + NEAR_TOP_MARGIN) {
        up = 0;
        apply(false);
        return;
      }
      if (isHeaderHeld()) return;
      if (desktop && desktop.matches) {
        up = 0;
        apply(false);
      } else if (dy > 0) {
        up = 0;
        apply(true);
      } else if (dy < 0 && !atPageEnd(y)) {
        up -= dy;
        if (up >= SHOW_AFTER_UP) apply(false);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    const unlisten = desktop
      ? listen(desktop, () => {
          if (desktop.matches) apply(false);
        })
      : () => {};
    return () => {
      window.removeEventListener('scroll', onScroll);
      unlisten();
    };
  }, [enabled, apply]);

  return hidden;
}
